import { useParams, Link, Navigate } from "react-router-dom";
import { Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Breadcrumb } from "@/components/Breadcrumb";
import { ARTICLES } from "@/data/content";
import { Seo, articleLd, breadcrumbLd } from "@/components/Seo";

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  if (!article) return <Navigate to="/knowledge" replace />;
  const related = ARTICLES.filter((a) => a.slug !== slug && a.tag === article.tag).slice(0, 2);

  return (
    <div>
      <Seo
        title={`${article.title} | Quantum Cover`}
        description={article.excerpt}
        path={`/knowledge/${article.slug}`}
        jsonLd={[
          articleLd(article, `/knowledge/${article.slug}`),
          breadcrumbLd([{ name: "Home", path: "/" }, { name: "Knowledge", path: "/knowledge" }, { name: article.title, path: `/knowledge/${article.slug}` }]),
        ]}
      />
      <section className="bg-[#0B3D2E] text-white pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Breadcrumb to="/knowledge" label="Knowledge Centre" current={article.tag} testId="article-breadcrumb" tone="dark" />
          <h1 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">{article.title}</h1>
          <p className="mt-4 flex items-center gap-1.5 text-sm text-slate-400"><Clock size={14} /> {article.read} read</p>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <div className="space-y-5 text-lg text-slate-700 leading-relaxed">
          {article.body.split("\n\n").map((block, i) => {
            const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
            const isOrdered = lines.length > 1 && lines.every((l) => /^\d+\.\s/.test(l));
            const isUnordered = lines.length > 1 && lines.every((l) => /^[-•]\s/.test(l));
            if (isOrdered)
              return (
                <ol key={i} className="list-decimal pl-6 space-y-3 marker:text-emerald-600 marker:font-semibold">
                  {lines.map((l, j) => <li key={j} className="pl-1">{l.replace(/^\d+\.\s/, "")}</li>)}
                </ol>
              );
            if (isUnordered)
              return (
                <ul key={i} className="list-disc pl-6 space-y-3 marker:text-emerald-600">
                  {lines.map((l, j) => <li key={j} className="pl-1">{l.replace(/^[-•]\s/, "")}</li>)}
                </ul>
              );
            return <p key={i}>{block}</p>;
          })}
        </div>

        <div className="mt-12 rounded-3xl bg-emerald-50 border border-emerald-100 p-8">
          <p className="text-sm text-emerald-800/80"><strong>Quick note:</strong> this is general information, not personal advice. Quantum Cover isn't a licensed Financial Advice Provider. When you want advice that fits your situation, we'll put you in touch with a licensed adviser.</p>
          <Link to="/booking"><Button className="mt-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-6">Book a Free Consultation</Button></Link>
        </div>

        {related.length > 0 && (
          <div className="mt-14">
            <h2 className="font-display text-xl font-semibold text-[#0B3D2E] mb-5">Keep reading</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {related.map((r) => (
                <Link key={r.slug} to={`/knowledge/${r.slug}`} className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-emerald-300 transition-colors">
                  <h3 className="font-display font-semibold text-[#0B3D2E]">{r.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600">{r.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
